import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { FaTrashAlt, FaEdit, FaPlus, FaCheck, FaTimes, FaHome } from 'react-icons/fa';
import Swal from 'sweetalert2';
import CategoriaForm from './CategoriaForm';

const PageContainer = styled.div`
  padding: 40px;
  min-height: 100vh;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const SearchBox = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
  flex-wrap: wrap;

  input {
    padding: 10px 12px;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    min-width: 260px;
    font-size: 0.95rem;
  }

  button {
    background: #34574A;
    color: #fff;
    border: none;
    border-radius: 8px;
    padding: 10px 16px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s;
  }

  button:hover {
    background: #27443a;
  }

  select {
    padding: 10px 12px;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    min-width: 180px;
    font-size: 0.95rem;
    background: #fff;
  }
`;

const AddButton = styled.button`
  background: #34574A;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-size: 1rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: background 0.2s;
  &:hover {
    background: #27443a;
  }
`;

const TableWrapper = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 900px;

  th, td {
    padding: 16px 12px;
    text-align: left;
    font-size: 0.95rem;
    border-bottom: 1px solid #eee;
  }

  th {
    background: #f8f9fa;
    color: #666;
    font-weight: 600;
    text-transform: uppercase;
  }

  tr:hover {
    background: #f8f9fa;
  }
`;

const ImgThumb = styled.img`
  width: 48px;
  height: 48px;
  object-fit: cover;
  border-radius: 8px;
  background: #f3f3f3;
  border: 1px solid #eee;
`;

const StatusButton = styled.button`
  background: ${props => props.$ativo ? '#22c55e' : '#c62828'};
  color: #fff;
  border: none;
  border-radius: 12px;
  font-size: 0.85rem;
  font-weight: 500;
  padding: 4px 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: background 0.2s;
  &:hover {
    opacity: 0.8;
  }
`;

const Tipo = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 10px;
  font-size: 0.85rem;
  font-weight: 600;
  background: ${props => props.$bg || '#f5f5f5'};
  color: ${props => props.$color || '#34574A'};
`;

const tipoCores = {
  comprar: { bg: '#e6f4ea', color: '#1e7e34' },
  arrendar: { bg: '#e8f0fe', color: '#1a56db' },
  ferias: { bg: '#fdf3e7', color: '#b45f06' },
};

const tipoNome = (cat) =>
  cat?.tipo?.nomeTIPOIMOVEL || cat?.tipo?.nome ||
  ({ 1: 'Comprar', 2: 'Arrendar', 3: 'Férias' }[Number(cat?.tipoCATEGORIA)]) ||
  cat?.tipoCATEGORIA || '-';

const tipoCor = (nome) =>
  tipoCores[String(nome || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()]
  || { bg: '#f5f5f5', color: '#34574A' };

const HomeBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  background: #fdf3e7;
  color: #b45f06;
  vertical-align: middle;
`;


const ActionCell = styled.td`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const IconButton = styled.button`
  background: none;
  border: none;
  padding: 6px;
  border-radius: 6px;
  cursor: pointer;
  color: #c0843d;
  font-size: 1.1rem;
  transition: background 0.2s;
  &:hover {
    background: #f8f8f8;
    color: #c62828;
  }
`;

const Categorias = () => {
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editData, setEditData] = useState(null);
  const [pagina, setPagina] = useState(1);
  const [busca, setBusca] = useState('');
  const [tipoFiltro, setTipoFiltro] = useState('');
  const categoriasPorPagina = 6;

  const categoriasFiltradas = React.useMemo(() => {
    const term = busca.trim().toLowerCase();
    const tipoOrder = ['férias', 'arrendar', 'comprar'];
    const filteredBusca = (!term ? categorias : categorias.filter(cat => {
      const nome = String(cat.nomeCATEGORIA || '').toLowerCase();
      const slug = String(cat.slugCATEGORIA || '').toLowerCase();
      const tipo = String(cat.tipo?.nomeTIPOIMOVEL || cat.tipo?.nome || cat.tipoCATEGORIA || '').toLowerCase();
      return nome.includes(term) || slug.includes(term) || tipo.includes(term);
    }));

    const filteredTipo = tipoFiltro
      ? filteredBusca.filter(cat => {
          const tipo = String(cat.tipo?.nomeTIPOIMOVEL || cat.tipo?.nome || cat.tipoCATEGORIA || '').toLowerCase();
          return tipo === tipoFiltro.toLowerCase();
        })
      : filteredBusca;

    const sorted = [...filteredTipo].sort((a, b) => {
      const tipoA = String(a.tipo?.nomeTIPOIMOVEL || a.tipo?.nome || a.tipoCATEGORIA || '').toLowerCase();
      const tipoB = String(b.tipo?.nomeTIPOIMOVEL || b.tipo?.nome || b.tipoCATEGORIA || '').toLowerCase();
      const idxA = tipoOrder.indexOf(tipoA);
      const idxB = tipoOrder.indexOf(tipoB);
      if (idxA !== idxB) return (idxA === -1 ? Infinity : idxA) - (idxB === -1 ? Infinity : idxB);
      const nomeA = String(a.nomeCATEGORIA || '').toLowerCase();
      const nomeB = String(b.nomeCATEGORIA || '').toLowerCase();
      return nomeA.localeCompare(nomeB);
    });

    return sorted;
  }, [busca, tipoFiltro, categorias]);

  useEffect(() => {
    setPagina(1);
  }, [tipoFiltro]);

  const totalPaginas = Math.max(1, Math.ceil(categoriasFiltradas.length / categoriasPorPagina));
  const categoriasPaginadas = categoriasFiltradas.slice((pagina - 1) * categoriasPorPagina, pagina * categoriasPorPagina);

  useEffect(() => {
    fetchCategorias();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const normalizeCatImage = (cat) => {
    const img = cat?.imagemCATEGORIA
      || cat?.imagem
      || cat?.image
      || cat?.image_url
      || cat?.urlImagem
      || cat?.url_imagem
      || cat?.thumbnail
      || cat?.thumb
      || '';
    
    // Se a imagem começar com /uploads/, monta URL completa
    if (img && img.startsWith('/uploads/')) {
      const baseUrl = import.meta.env.VITE_URL_API?.replace('/api', '');
      return `${baseUrl}${img}`;
    }
    
    return img;
  };

  const fetchCategorias = async () => {
    setLoading(true);
    setError(null);
    const token = localStorage.getItem('auth_token');
    try {
      const response = await fetch(`${import.meta.env.VITE_URL_API}/categorias/admin`, {
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) throw new Error('Erro ao buscar categorias');
      const data = await response.json();
      
      // Log para depurar categorias do backend
      console.log('[Categorias] Dados do backend:', data);
      const categoria71 = data.find(cat => cat.codigoCATEGORIA === 71);
      if (categoria71) {
        console.log('[Categorias] Categoria 71 do backend:', categoria71);
        console.log('[Categorias] imagemCATEGORIA da categoria 71:', categoria71.imagemCATEGORIA);
      }
      
      const normalized = (Array.isArray(data) ? data : [])
        .filter((cat) => cat && (cat.codigoCATEGORIA !== undefined && cat.codigoCATEGORIA !== null))
        .map((cat) => {
          const normalizedImage = normalizeCatImage(cat);
          if (cat.codigoCATEGORIA === 71) {
            console.log('[Categorias] normalizeCatImage resultado para categoria 71:', normalizedImage);
          }
          return {
            ...cat,
            nomeCATEGORIA: cat.nomeCATEGORIA || '-',
            slugCATEGORIA: cat.slugCATEGORIA || '-',
            tipoCATEGORIA: cat.tipoCATEGORIA || cat?.tipo?.nomeTIPOIMOVEL || cat?.tipo?.nome || '-',
            ativoCATEGORIA: Number(cat.ativoCATEGORIA ?? cat.ativo ?? cat.active ?? cat.status ?? 0),
            imagemCATEGORIA: normalizedImage,
          };
        });
      setCategorias(normalized);
    } catch (err) {
      setError('Erro ao buscar categorias.');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAtivo = async (cat) => {
    const token = localStorage.getItem('auth_token');
    if (!token) return;
    const novoStatus = cat.ativoCATEGORIA === 1 ? 0 : 1;
    try {
      await fetch(`${import.meta.env.VITE_URL_API}/categorias/${cat.codigoCATEGORIA}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'accept': 'application/json'
        },
        body: JSON.stringify({
          codigoCATEGORIA: cat.codigoCATEGORIA,
          ativoCATEGORIA: novoStatus
        })
      });
      setTimeout(fetchCategorias, 500);
    } catch (err) {
      Swal.fire('Erro', 'Não foi possível atualizar o status.', 'error');
    }
  };

  const handleDelete = (cat) => {
    Swal.fire({
      title: 'Tem certeza?',
      text: `Deseja realmente excluir a categoria "${cat.nomeCATEGORIA}"? Esta ação não poderá ser desfeita!`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#c62828',
      cancelButtonColor: '#34574A',
      confirmButtonText: 'Sim, excluir',
      cancelButtonText: 'Cancelar',
      reverseButtons: true
    }).then(async (result) => {
      if (result.isConfirmed) {
        const token = localStorage.getItem('auth_token');
        if (!token) return;
        try {
          await fetch(`${import.meta.env.VITE_URL_API}/categorias/${cat.codigoCATEGORIA}`, {
            method: 'DELETE',
            headers: {
              'accept': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          });
          Swal.fire('Excluída!', 'Categoria excluída com sucesso.', 'success');
          fetchCategorias();
        } catch {
          Swal.fire('Erro', 'Não foi possível excluir a categoria.', 'error');
        }
      }
    });
  };

  const handleEdit = (cat) => {
    const imagem = normalizeCatImage(cat);
    setEditData({
      ...cat,
      tipoCATEGORIA: cat.tipo?.codigo || cat.tipoCATEGORIA,
      imagemCATEGORIA: imagem,
    });
    setShowForm(true);
  };

  const handleAdd = () => {
    setEditData(null);
    setShowForm(true);
  };

  const handleFormSubmit = async (formData) => {
    const token = localStorage.getItem('auth_token');

    // Se não tiver token, nem tenta chamar API
    if (!token) {
      Swal.fire('Sessão expirada', 'Faça login novamente para salvar a categoria.', 'warning');
      return;
    }

    const formDataToSend = new FormData();
    const slugify = (text) => String(text || '')
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .replace(/[^\w\s-]/g, '')
      .trim()
      .replace(/\s+/g, '-')
      .toLowerCase();

    const nomeRaw = String(formData.nomeCATEGORIA || '').trim();
    const slugRaw = String(formData.slugCATEGORIA || '').trim();
    const nome = nomeRaw;
    const slug = slugRaw || slugify(nomeRaw);
    const tipo = formData.tipoCATEGORIA;
    const tipoString = (tipo || tipo === 0) ? String(tipo) : '';

    if (!nome || !slug || !tipoString) {
      Swal.fire('Campos obrigatórios', 'Preencha Nome, Slug e Tipo antes de salvar.', 'warning');
      return;
    }

    formDataToSend.append('nomeCATEGORIA', nome);
    formDataToSend.append('slugCATEGORIA', slug);
    formDataToSend.append('tipoCATEGORIA', tipoString);
    if (editData?.codigoCATEGORIA != null) {
      formDataToSend.append('codigoCATEGORIA', String(editData.codigoCATEGORIA));
    }

    // Campo opcional: ordem
    if (formData.ordemCATEGORIA !== '' && formData.ordemCATEGORIA != null) {
      formDataToSend.append('ordemCATEGORIA', String(formData.ordemCATEGORIA));
    }

    // Flags sempre com default
    formDataToSend.append('destaqueCATEGORIA', String(formData.destaqueCATEGORIA ?? 0));
    formDataToSend.append('ativoCATEGORIA', String(formData.ativoCATEGORIA ?? 1));

    // Loga o payload antes de enviar para depuração
    try {
      const entries = [];
      formDataToSend.forEach((v, k) => entries.push([k, v]));
      console.log('[Categorias] Enviando FormData entries:', entries);
      console.log('[Categorias] arquivoImagem no formData:', formData.arquivoImagem);
      console.log('[Categorias] arquivoImagem é File?', formData.arquivoImagem instanceof File);
      console.log('[Categorias] Todos os dados do formData:', {
        nomeCATEGORIA: formData.nomeCATEGORIA,
        slugCATEGORIA: formData.slugCATEGORIA,
        tipoCATEGORIA: formData.tipoCATEGORIA,
        codigoCATEGORIA: formData.codigoCATEGORIA,
        destaqueCATEGORIA: formData.destaqueCATEGORIA,
        ativoCATEGORIA: formData.ativoCATEGORIA,
        ordemCATEGORIA: formData.ordemCATEGORIA
      });
    } catch (e) {
      console.error('[Categorias] Erro ao logar payload:', e);
    }

    // Imagem:
    // - Backend usa upload.any() e lê req.file || req.files[0]; o campo mais seguro é "file".
    // - Enviamos também em "imagem" para compatibilidade com upload.single('imagem').
    // - Se não houver arquivo novo, mas existe URL salva, enviamos em imagemCATEGORIA para manter a anterior.
    //   Caminhos /tipos/* são assets do portal (imagem padrão), não imagens salvas na API.
    if (formData.arquivoImagem) {
      formDataToSend.append('file', formData.arquivoImagem);
      formDataToSend.append('imagem', formData.arquivoImagem);
    } else if (formData.imagemCATEGORIA && !String(formData.imagemCATEGORIA).startsWith('/tipos/')) {
      formDataToSend.append('imagemCATEGORIA', formData.imagemCATEGORIA);
    }

    try {
      const urlBase = import.meta.env.VITE_URL_API;
      const isEdit = Boolean(editData);
      const url = isEdit
        ? `${urlBase}/categorias/${editData.codigoCATEGORIA}`
        : `${urlBase}/categorias`;

      console.log('[Categorias] URL completa:', url);
      console.log('[Categorias] Método é edição?', isEdit);
      console.log('[Categorias] códigoCATEGORIA:', editData?.codigoCATEGORIA);

      const response = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: {
          // Não definimos Content-Type manualmente para permitir que o browser
          // monte o boundary correto do multipart/form-data.
          accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: formDataToSend,
      });

      const responseText = await response.text();
      console.log('[Categorias] Resposta bruta da API:', responseText);
      console.log('[Categorias] Status HTTP:', response.status);
      console.log('[Categorias] Headers da resposta:', Object.fromEntries(response.headers.entries()));
      
      let jsonData;
      try {
        jsonData = JSON.parse(responseText);
      } catch (e) {
        console.log('[Categorias] Erro ao parsear JSON:', e);
        jsonData = null;
      }

      if (!response.ok) {
        const errorText = jsonData ? JSON.stringify(jsonData) : responseText;
        console.error('Erro ao salvar categoria', response.status, errorText);

        if (response.status === 401) {
          Swal.fire('Sessão expirada', 'Faça login novamente para salvar a categoria.', 'warning');
        } else if (response.status === 404) {
          Swal.fire(
            'Erro',
            'Não foi possível salvar a categoria (rota de categorias não encontrada).',
            'error'
          );
        } else {
          Swal.fire('Erro', 'Não foi possível salvar a categoria.', 'error');
        }
        return;
      }

      // Backend agora deve retornar JSON válido com a nova rota /upload
      const catResp = jsonData?.categoria || jsonData || {};
      console.log('[Categorias] Resposta da API:', jsonData);
      console.log('[Categorias] catResp:', catResp);
      console.log('[Categorias] imagemCATEGORIA na resposta:', catResp.imagemCATEGORIA);
      
      // Monta a categoria completa com dados antigos + nova imagem
      const imagemUrl = catResp.imagemCATEGORIA 
        ? (catResp.imagemCATEGORIA.startsWith('http') 
            ? catResp.imagemCATEGORIA 
            : `${import.meta.env.VITE_URL_API?.replace('/api', '') || 'http://localhost:8090'}${catResp.imagemCATEGORIA}`)
        : normalizeCatImage(catResp);
        
      const normalizedCat = {
        ...editData, // Mantém todos os dados originais da categoria
        ...catResp,  // Sobrescreve com os dados da resposta (incluindo imagemCATEGORIA)
        imagemCATEGORIA: imagemUrl, // Usa a URL completa da nova imagem
      };
      console.log('[Categorias] URL completa da imagem:', imagemUrl);
      console.log('[Categorias] normalizedCat completo:', normalizedCat);
      console.log('[Categorias] normalizedCat.imagemCATEGORIA:', normalizedCat.imagemCATEGORIA);

      setCategorias(prev => {
        if (!Array.isArray(prev)) return [normalizedCat];
        if (editData?.codigoCATEGORIA != null) {
          return prev.map(c => (c.codigoCATEGORIA === editData.codigoCATEGORIA ? normalizedCat : c));
        }
        return [normalizedCat, ...prev];
      });

      // Atualiza o editData para o formulário mostrar a nova imagem imediatamente
      setEditData(normalizedCat);

      Swal.fire('Sucesso', editData ? 'Categoria atualizada com sucesso!' : 'Categoria criada com sucesso!', 'success');
      setShowForm(false);

      // Se renomeou ou trocou a foto, oferecer propagar para as categorias irmãs
      // (mesma família de slug em outros tipos — ex.: "Apartamentos" Comprar/Arrendar/Férias)
      const nomeAntigo = String(editData?.nomeCATEGORIA || '').trim();
      const imagemAntiga = String(editData?.imagemCATEGORIA || '').trim();
      const imagemNova = String(normalizedCat?.imagemCATEGORIA || '').trim();
      const nomeMudou = nomeAntigo && nomeAntigo !== nome;
      // Considera mudança de imagem quando um novo arquivo foi enviado ou a URL salva mudou de fato
      const imagemMudou = Boolean(formData.arquivoImagem) ||
        (imagemNova && imagemAntiga && imagemNova !== imagemAntiga);
      if (isEdit && (nomeMudou || imagemMudou)) {
        const slugRaiz = (s) => String(s?.slugCATEGORIA || '').replace(/-\d+$/, '');
        const irmaos = categorias.filter(c =>
          c.codigoCATEGORIA !== editData.codigoCATEGORIA &&
          slugRaiz(c) && slugRaiz(c) === slugRaiz(editData)
        );
        if (irmaos.length) {
          const oQueMuda = [
            nomeMudou ? `nome "<b>${nome}</b>"` : null,
            imagemMudou ? 'a imagem' : null,
          ].filter(Boolean).join(' e ');
          const { isConfirmed } = await Swal.fire({
            title: 'Atualizar categorias irmãs?',
            html: `Existem outras categorias da mesma família em outros tipos:<br/>` +
              irmaos.map(s => `#${s.codigoCATEGORIA} · ${s.nomeCATEGORIA} (${tipoNome(s)})`).join('<br/>') +
              `<br/><br/>Aplicar ${oQueMuda} em todas?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#34574A',
            cancelButtonColor: '#bbb',
            confirmButtonText: 'Sim, atualizar todas',
            cancelButtonText: 'Só esta',
          });
          if (isConfirmed) {
            const payloadIrmaos = {};
            if (nomeMudou) payloadIrmaos.nomeCATEGORIA = nome;
            // Envia o valor cru retornado pela API (relativo /uploads/... ou URL
            // absoluta do Supabase), nunca a URL normalizada com o host deste ambiente
            if (imagemMudou) payloadIrmaos.imagemCATEGORIA = catResp.imagemCATEGORIA || imagemNova;
            try {
              await Promise.all(irmaos.map(s =>
                fetch(`${urlBase}/categorias/${s.codigoCATEGORIA}`, {
                  method: 'PATCH',
                  headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    'accept': 'application/json',
                  },
                  body: JSON.stringify(payloadIrmaos),
                })
              ));
              fetchCategorias();
              Swal.fire('Pronto', 'Categorias irmãs atualizadas.', 'success');
            } catch {
              Swal.fire('Erro', 'Não foi possível atualizar todas as categorias irmãs.', 'error');
            }
          }
        }
      }
    } catch {
      Swal.fire('Erro', 'Não foi possível salvar a categoria.', 'error');
    }
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditData(null);
  };

  const handleSearch = (e) => {
    e?.preventDefault();
    setPagina(1);
  };

  return (
    <PageContainer>
      <HeaderRow>
        <h1>Categorias</h1>
        <AddButton onClick={handleAdd}>
          <FaPlus /> Nova Categoria
        </AddButton>
      </HeaderRow>

      <SearchBox>
        <form onSubmit={handleSearch} style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="text"
            placeholder="Buscar por nome, slug ou tipo..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
          <select value={tipoFiltro} onChange={(e) => setTipoFiltro(e.target.value)}>
            <option value="">Todos os tipos</option>
            <option value="férias">Férias</option>
            <option value="arrendar">Arrendar</option>
            <option value="comprar">Comprar</option>
          </select>
          <button type="submit">Pesquisar</button>
        </form>
      </SearchBox>
      {showForm && (
        <div style={{
          position:'fixed',
          top:0,
          left:0,
          width:'100vw',
          height:'100vh',
          background:'rgba(0,0,0,0.25)',
          zIndex:1000,
          display:'flex',
          alignItems:'center',
          justifyContent:'center',
          padding: '20px'
        }}>
          <div style={{
            background:'#fff',
            padding:32,
            borderRadius:12,
            minWidth:360,
            maxWidth: '600px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}>
            <h2 style={{marginBottom:4, color: '#333', fontSize: '1.5rem'}}>{editData ? 'Editar Categoria' : 'Nova Categoria'}</h2>
            {editData && (
              <p style={{marginBottom:16, color: '#718078', fontSize: '0.9rem'}}>
                #{editData.codigoCATEGORIA} · {tipoNome(editData)}
                {Number(editData.destaqueCATEGORIA) === 1 && ' · exibida na home'}
              </p>
            )}
            <CategoriaForm onSubmit={handleFormSubmit} onCancel={handleFormCancel} initialData={editData} />
          </div>
        </div>
      )}
      {loading && <div>Carregando...</div>}
      {error && <div style={{color:'red'}}>{error}</div>}
      {!loading && !error && (
        <TableWrapper>
          <Table>
            <thead>
              <tr>
                <th>ID</th>
                <th>Nome</th>
                <th>Slug</th>
                <th>Tipo</th>
                <th>Status</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {categoriasPaginadas.map((cat, idx) => (
                <tr key={cat.codigoCATEGORIA ?? cat.slugCATEGORIA ?? `${cat.nomeCATEGORIA}-${idx}`}>
                  <td>{cat.codigoCATEGORIA}</td>
                  <td>
                    {cat.nomeCATEGORIA}
                    {Number(cat.destaqueCATEGORIA) === 1 && (
                      <HomeBadge title="Esta categoria aparece na grade da página inicial"><FaHome /> Na home{cat.ordemCATEGORIA != null ? ` · ${cat.ordemCATEGORIA}º` : ''}</HomeBadge>
                    )}
                  </td>
                  <td>{cat.slugCATEGORIA}</td>
                  <td><Tipo $bg={tipoCor(tipoNome(cat)).bg} $color={tipoCor(tipoNome(cat)).color}>{tipoNome(cat)}</Tipo></td>
                  <td>
                    <StatusButton
                      $ativo={cat.ativoCATEGORIA === 1}
                      onClick={() => handleToggleAtivo(cat)}
                      title={cat.ativoCATEGORIA === 1 ? 'Desativar' : 'Ativar'}
                    >
                      {cat.ativoCATEGORIA === 1 ? <FaCheck /> : <FaTimes />} {cat.ativoCATEGORIA === 1 ? 'Ativo' : 'Inativo'}
                    </StatusButton>
                  </td>
                  <ActionCell>
                    <IconButton onClick={() => handleEdit(cat)} title="Editar">
                      <FaEdit />
                    </IconButton>
                    <IconButton onClick={() => handleDelete(cat)} title="Excluir">
                      <FaTrashAlt />
                    </IconButton>
                  </ActionCell>
                </tr>
              ))}
            </tbody>
          </Table>
          {/* Paginação */}
          <div style={{display:'flex',justifyContent:'center',alignItems:'center',gap:8,margin:'24px 0'}}>
            <button onClick={()=>setPagina(p=>Math.max(1,p-1))} disabled={pagina===1} style={{padding:'6px 14px',borderRadius:6,border:'1px solid #eee',background:'#fff',cursor:pagina===1?'not-allowed':'pointer'}}>Anterior</button>
            {[...Array(totalPaginas)].map((_,i)=>(
              <button
                key={i}
                onClick={()=>setPagina(i+1)}
                style={{padding:'6px 12px',borderRadius:6,border:'1.5px solid '+(pagina===i+1?'#34574A':'#eee'),background:pagina===i+1?'#34574A':'#fff',color:pagina===i+1?'#fff':'#222',fontWeight:600,cursor:'pointer'}}
              >{i+1}</button>
            ))}
            <button onClick={()=>setPagina(p=>Math.min(totalPaginas,p+1))} disabled={pagina===totalPaginas} style={{padding:'6px 14px',borderRadius:6,border:'1px solid #eee',background:'#fff',cursor:pagina===totalPaginas?'not-allowed':'pointer'}}>Próxima</button>
          </div>
        </TableWrapper>
      )}
    </PageContainer>
  );
};

export default Categorias;
